import { useQueryClient } from "@tanstack/react-query";
import BigNumber from "bignumber.js";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { t } from "ttag";
import { parseUnits } from "viem";
import {
  useAccount,
  useBlockNumber,
  useConfig,
  useReadContract,
  useSimulateContract,
  useWriteContract,
} from "wagmi";
import { waitForTransactionReceipt } from "wagmi/actions";

import { PendingTxType } from "@/components/trade/partials/pending-tx";
import { GAS_APPROVE } from "@/constants/gas";
import { ZERO } from "@/constants/math";
import { useRx } from "@/contexts/rx";
import { allowanceSelector } from "@/contracts/selectors/erc20";
import { convertWithDecimals } from "@/contracts/transform/common";
import { is0X } from "@/util/check";
import { withExactMessage } from "@/util/error";

import { useDisplayError } from "./error";
import { useGas } from "./gas";
import { usePushSubject } from "./rx";
import { TokenDetails, useBalanceNormalize } from "./token";

const APPROVE_ABI = [
  {
    inputs: [
      {
        internalType: "address",
        name: "spender",
        type: "address",
      },
      {
        internalType: "uint256",
        name: "amount",
        type: "uint256",
      },
    ],
    name: "approve",
    outputs: [
      {
        internalType: "bool",
        name: "",
        type: "bool",
      },
    ],
    stateMutability: "nonpayable",
    type: "function",
  },
];

const USDT_APPROVE_ABI = [
  {
    inputs: [
      {
        internalType: "address",
        name: "_spender",
        type: "address",
      },
      {
        internalType: "uint256",
        name: "_value",
        type: "uint256",
      },
    ],
    name: "approve",
    outputs: [],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
];

interface State {
  visible?: boolean;
  pending?: boolean;
}

const reducer = (state: State, action: Partial<State>) => ({
  ...state,
  ...action,
});

const USDT_MAINNET = "0xdAC17F958D2ee523a2206206994597C13D831ec7";

const getAbiByAddr = (
  addr: `0x${string}` | undefined,
): typeof USDT_APPROVE_ABI | typeof APPROVE_ABI => {
  if (addr === USDT_MAINNET) {
    // https://etherscan.io/address/0xdac17f958d2ee523a2206206994597c13d831ec7#code
    return USDT_APPROVE_ABI;
  }

  return APPROVE_ABI;
};

// USDT must be revoked to make new approve
const amountWithRevokePossibility = (
  tokenAddress: `0x${string}` | undefined,
  amount: BigNumber | undefined,
  allowance: BigNumber,
): BigNumber | undefined => {
  if (!amount || !tokenAddress) return undefined;

  if (tokenAddress === USDT_MAINNET && allowance.gt(0)) return ZERO;

  return amount;
};

export const useApprove = ({
  token,
  spender,
  amount,
  refetch,
  oneStepSubmit,
}: {
  token?: TokenDetails | `0x${string}`;
  spender?: `0x${string}`;
  amount?: string;
  refetch?: () => Promise<void>;
  oneStepSubmit?: boolean;
}) => {
  const cancelRef = useRef<() => void>();
  const [state, dispatch] = useReducer(reducer, {});
  const { error$ } = useRx();
  const { gas, gasPrice, useError } = useGas(GAS_APPROVE);
  const { address } = useAccount();
  const tokenAddress = is0X(token) ? token : token?.address;
  const callback = useRef<() => void>();

  const params = useMemo(() => {
    return {
      ...allowanceSelector(tokenAddress, address, spender),
    };
  }, [tokenAddress, address, spender]);

  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({ watch: true });

  const _allowance = useReadContract({
    ...params,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: _allowance.queryKey });
  }, [blockNumber, queryClient]);

  usePushSubject(error$, _allowance.error);

  const balance = useBalanceNormalize({
    address,
    token: tokenAddress,
    query: {
      enabled: Boolean(state.visible),
    },
  });

  const decimals = balance?.data?.decimals;
  const symbol = balance?.data?.symbol;

  const allowance = useMemo(
    () => convertWithDecimals(_allowance.data, decimals),
    [_allowance.data, decimals],
  );
  const approveAmount = useMemo(() => {
    const num = Number(amount);

    if (!Number.isFinite(num) || num <= 0) {
      return;
    }

    return ZERO.plus(amount!);
  }, [amount]);

  const needApprove = token ? allowance?.lt(approveAmount ?? 0) : false;

  const hasBalance = Boolean(
    balance?.data && approveAmount?.lte(balance?.data?.formatted),
  );

  const args = useMemo(() => {
    return spender && approveAmount && decimals
      ? ([
          spender!,
          parseUnits(
            amountWithRevokePossibility(tokenAddress, approveAmount, allowance)
              ?.dp(decimals ?? 18, 2)
              ?.toString() ?? "0",
            decimals ?? 18,
          ),
        ] as const)
      : undefined;
  }, [spender, approveAmount, decimals, tokenAddress, allowance]);

  const approve = useSimulateContract({
    abi: getAbiByAddr(tokenAddress),
    args,
    address: tokenAddress,
    functionName: "approve",
    query: {
      enabled: Boolean(approveAmount?.gt(0) && spender),
    },
    gas,
    gasPrice,
  });

  useError(approve.error);
  const tx = useWriteContract();
  const { lastError, withMessage } = useDisplayError(approve.refetch);

  const setCallback = useCallback((fn?: () => void) => {
    callback.current = fn;
  }, []);

  const closeModalWithCallback = useCallback(() => {
    dispatch({ visible: false });
    callback.current?.();
  }, [dispatch]);

  const _submit = useMemo(() => {
    if (!needApprove) {
      return state.visible ? closeModalWithCallback : undefined;
    }
    if (!state.visible && !oneStepSubmit) {
      return dispatch.bind(null, { visible: true });
    }
    if (!hasBalance) {
      return withMessage.bind(null, t`Not enough balance`);
    }
    if (approve.error) {
      return lastError;
    }
    if (!approve.data) {
      return undefined;
    }
    return () => tx.writeContractAsync(approve.data.request);
  }, [
    needApprove,
    state.visible,
    oneStepSubmit,
    hasBalance,
    approve.error,
    approve.data,
    closeModalWithCallback,
    withMessage,
    lastError,
    tx,
  ]);

  const pendingTxType = state.pending
    ? PendingTxType.Pending
    : state.visible
    ? needApprove
      ? PendingTxType.AwaitingApprove
      : PendingTxType.Approved
    : undefined;

  const config = useConfig();
  const submit = useCallback(() => {
    const promise = _submit?.() as undefined | Promise<`0x${string}`>;
    if (promise) {
      const cancel = new Promise<void>((resolve) => {
        cancelRef.current = resolve;
      });

      dispatch({ pending: true });

      return Promise.race([promise, cancel])
        .then((res) => {
          if (res) {
            return waitForTransactionReceipt(config, {
              hash: res,
              confirmations: 2,
            });
          } else {
            tx.reset();
            throw withExactMessage(
              new Error(t`You canceled the action.`),
              `/images/cancel.svg`,
            );
          }
        })
        .then((receipt) => {
          Promise.all([
            _allowance.refetch(),
            refetch?.() ?? Promise.resolve(),
          ]).then(() => {
            dispatch({ pending: false });
          });

          // fixme resolve receipt doen't work
          return receipt;
        })
        .catch((e) => {
          dispatch({ pending: false, visible: false });
          console.error(e);
          error$.next(e);
        });
    }

    return Promise.resolve();
  }, [_submit, error$, tx, _allowance, refetch]);

  const cancel = useCallback(() => {
    if (state.pending) {
      cancelRef.current?.();
    } else {
      dispatch({ visible: false });
    }
  }, [state.pending]);

  const ret = {
    allowance,
    cancel,
    needApprove,
    approveAmount,
    pendingTxType,
    submit,
    title: needApprove
      ? t`To allow smart contract to spend your ${symbol}s.`
      : t`Now you can continue.`,
    ...state,
    setCallback,
  };

  return ret;
};
